import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  VStack,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useVoiceService } from '../../../hooks/useVoiceService';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGroupCreated: () => void;
  roomId: string;
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onGroupCreated,
  roomId,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 2, // Voice
    settings: {
      max_members: 20,
      is_public: true,
      allow_guest: false,
      require_approval: false,
      join_code: '',
      audio_quality: 2, // High
      allowed_roles: [] as string[],
    },
    initial_member_ids: [] as string[],
    is_private: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const { createVoiceGroup } = useVoiceService();
  const toast = useToast();

  const generateJoinCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setFormData(prev => ({
      ...prev,
      settings: { ...prev.settings, join_code: code }
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoading(true);
      
      await createVoiceGroup({
        ...formData,
        room_id: roomId,
      });
      
      onGroupCreated();
    } catch (error) {
      // Error is already handled by the service hook
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      type: 2,
      settings: {
        max_members: 20,
        is_public: true,
        allow_guest: false,
        require_approval: false,
        join_code: '',
        audio_quality: 2,
        allowed_roles: [],
      },
      initial_member_ids: [],
      is_private: false,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Voice Group</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Group Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter group name..."
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter group description..."
                rows={3}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Group Type</FormLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: parseInt(e.target.value) }))}
              >
                <option value={1}>Text</option>
                <option value={2}>Voice</option>
                <option value={3}>Video</option>
                <option value={4}>Streaming</option>
              </Select>
            </FormControl>

            <Divider />
            <Text fontWeight="medium">Group Settings</Text>

            <HStack>
              <FormControl>
                <FormLabel>Max Members</FormLabel>
                <NumberInput
                  value={formData.settings.max_members}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, max_members: value || 20 }
                  }))}
                  min={1}
                  max={100}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Audio Quality</FormLabel>
                <Select
                  value={formData.settings.audio_quality}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, audio_quality: parseInt(e.target.value) }
                  }))}
                >
                  <option value={1}>Low</option>
                  <option value={2}>High</option>
                  <option value={3}>Ultra</option>
                </Select>
              </FormControl>
            </HStack>

            <VStack align="stretch" spacing={3}>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="is-public" mb="0">
                  Public Group
                </FormLabel>
                <Switch
                  id="is-public"
                  isChecked={formData.settings.is_public}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, is_public: e.target.checked }
                  }))}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="allow-guest" mb="0">
                  Allow Guests
                </FormLabel>
                <Switch
                  id="allow-guest"
                  isChecked={formData.settings.allow_guest}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, allow_guest: e.target.checked }
                  }))}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="require-approval" mb="0">
                  Require Approval to Join
                </FormLabel>
                <Switch
                  id="require-approval"
                  isChecked={formData.settings.require_approval}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, require_approval: e.target.checked }
                  }))}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="is-private" mb="0">
                  Private Group
                </FormLabel>
                <Switch
                  id="is-private"
                  isChecked={formData.is_private}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_private: e.target.checked }))}
                />
              </FormControl>
            </VStack>

            {!formData.settings.is_public && (
              <FormControl>
                <FormLabel>Join Code</FormLabel>
                <HStack>
                  <Input
                    value={formData.settings.join_code}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      settings: { ...prev.settings, join_code: e.target.value }
                    }))}
                    placeholder="Enter join code or generate one"
                  />
                  <Button onClick={generateJoinCode} size="sm">
                    Generate
                  </Button>
                </HStack>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Users will need this code to join the private group
                </Text>
              </FormControl>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Creating..."
          >
            Create Group
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};